import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function ymd(date) {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `${y}-${m}-${d}`;
}

function fmtMonthName(year, month) {
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString("cs-CZ", { month: "long", year: "numeric" });
}

function getMonthMeta(year, month) {
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  // pondělí = 0
  const mondayIndex = (first.getDay() + 6) % 7;
  return { first, last, mondayIndex, daysInMonth: last.getDate() };
}

function easterSunday(year) {
  // Anonymous Gregorian algorithm
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=March, 4=April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function isCzechHoliday(dateObj) {
  const y = dateObj.getFullYear();
  const m = dateObj.getMonth() + 1;
  const d = dateObj.getDate();

  // fixní svátky
  const fixed = new Set([
    "1-1", // Nový rok
    "5-1", // Svátek práce
    "5-8", // Den vítězství
    "7-5", // Cyrila a Metoděje
    "7-6", // Mistra Jana Husa
    "9-28", // Den české státnosti
    "10-28", // Vznik ČSR
    "11-17", // Boj za svobodu a demokracii
    "12-24", // Štědrý den
    "12-25", // 1. svátek vánoční
    "12-26", // 2. svátek vánoční
  ]);

  if (fixed.has(`${m}-${d}`)) return true;

  // pohyblivé: Velký pátek + Velikonoční pondělí
  const easter = easterSunday(y);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);

  return ymd(goodFriday) === ymd(dateObj) || ymd(easterMonday) === ymd(dateObj);
}

function getCzechHolidayName(dateObj) {
  const y = dateObj.getFullYear();
  const m = dateObj.getMonth() + 1;
  const d = dateObj.getDate();

  const fixed = {
    "1-1": "Nový rok / Den obnovy samostatného českého státu",
    "5-1": "Svátek práce",
    "5-8": "Den vítězství",
    "7-5": "Den Cyrila a Metoděje",
    "7-6": "Den upálení mistra Jana Husa",
    "9-28": "Den české státnosti",
    "10-28": "Den vzniku Československa",
    "11-17": "Den boje za svobodu a demokracii",
    "12-24": "Štědrý den",
    "12-25": "1. svátek vánoční",
    "12-26": "2. svátek vánoční",
  };

  const fixedName = fixed[`${m}-${d}`];
  if (fixedName) return fixedName;

  const easter = easterSunday(y);

  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);

  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);

  if (ymd(goodFriday) === ymd(dateObj)) return "Velký pátek";
  if (ymd(easterMonday) === ymd(dateObj)) return "Velikonoční pondělí";

  return null;
}

function normalizeShiftType(t) {
  const s = String(t || "").toUpperCase();
  if (s === "R" || s === "O" || s === "C" || s === "?") return s;
  // fallbacky pokud by to někdy bylo plným slovem
  if (s.startsWith("R")) return "R";
  if (s.startsWith("O")) return "O";
  if (s.startsWith("C")) return "C";
  return s || "";
}

function shortName(full) {
  const s = String(full || "").trim();
  if (!s) return "";
  const parts = s.split(/\s+/);
  if (parts.length === 1) return parts[0];
  // příjmení + 1. písmeno jména
  return `${parts[parts.length - 1]} ${parts[0][0]}.`;
}

function normalizeBranchCode(value) {
  return String(value ?? "").trim().replace(/^0+(?=\d)/, "");
}

function getNicknameForBranch(emp, pharmacyCode) {
  const target = normalizeBranchCode(pharmacyCode);
  if (!target) return "";

  const list = Array.isArray(emp?.branchNicknames) ? emp.branchNicknames : [];
  const found = list.find(
    (x) => normalizeBranchCode(x?.pharmacyCode) === target
  );

  return String(found?.nickname || "").trim();
}

function normalizeRole(pos) {
  const s = String(pos || "").toLowerCase();

  // magistr / pharmacist / farmaceut
  if (
    s.includes("mag") ||
    s.includes("pharm") ||
    s.includes("mgr") ||
    s.includes("farmaceut") ||
    s.includes("farmaceu")
  )
    return "MAG";

  // asistent
  if (s.includes("asist")) return "AS";

  // sanitář
  if (s.includes("sanit")) return "SAN";

  return "OTHER";
}

function extractPlanPayload(data) {
  // backend může vracet:
  // A) { plan: {...} }
  // B) přímo {...planFields}
  // C) { plan: null, planDoc: {...} } nebo { plan: null, item: {...} } apod.

  const candidates = [
    data?.plan,
    data?.planDoc,
    data?.item,
    data?.doc,
    data?.result,
    data,
  ].filter(Boolean);

  // vezmeme první objekt, který vypadá jako ShiftPlan (má year/month nebo snapshot)
  const plan =
    candidates.find((x) => x?.snapshot || (x?.year && x?.month) || x?._id) ||
    null;

  const shiftsRaw =
    plan?.snapshot?.shifts ||
    plan?.shifts ||
    data?.snapshot?.shifts ||
    data?.shifts ||
    {};

  const employeesRaw =
    plan?.snapshot?.employees ||
    plan?.employees ||
    data?.snapshot?.employees ||
    data?.employees ||
    [];

  return { plan, shiftsRaw, employeesRaw };
}

function pickName(e) {
  return e?.fullName || e?.name || e?.jmeno || e?.employeeName || e?.label || "";
}

function pickId(e) {
  return e?._id || e?.id || e?.employeeId || "";
}

function flattenShifts(shiftsRaw, employeesRaw) {
  // shiftsRaw: { "YYYY-MM-DD": { employees: { empKey: { type, hours } } } }
  // employeesRaw (snapshot) je ARRAY objektů: { employeeId, fullName, ... }
  // employeeId je ObjectId => v JSONu typicky string "662c..."

  const nameById = new Map();

  const asStr = (v) => {
    if (v === undefined || v === null) return "";
    return String(v).trim();
  };

  const addName = (id, name) => {
    const k = asStr(id);
    const n = asStr(name);
    if (k && n) nameById.set(k, n);
  };

  if (Array.isArray(employeesRaw)) {
    for (const e of employeesRaw) {
      const id = e?.employeeId ?? e?._id ?? e?.id;
      const name = e?.fullName ?? e?.name ?? e?.jmeno ?? "";
      addName(id, name);

      if (e?.employeeId?.$oid) addName(e.employeeId.$oid, name);
    }
  }

  if (employeesRaw && typeof employeesRaw === "object" && !Array.isArray(employeesRaw)) {
    for (const [id, e] of Object.entries(employeesRaw)) {
      const name = e?.fullName ?? e?.name ?? e?.jmeno ?? "";
      addName(id, name);
      if (e?.employeeId) addName(e.employeeId, name);
      if (e?.employeeId?.$oid) addName(e.employeeId.$oid, name);
    }
  }

  const out = [];
  if (!shiftsRaw || typeof shiftsRaw !== "object") return out;

  for (const [dateIso, day] of Object.entries(shiftsRaw)) {
    const emps = day?.employees;
    if (!emps || typeof emps !== "object") continue;

    for (const [empKey, rec] of Object.entries(emps)) {
      const type = rec?.type;
      if (!type) continue;

      const empId = asStr(empKey);

      out.push({
        date: asStr(dateIso).slice(0, 10),
        employeeId: empId,
        employeeName: nameById.get(empId) || "",
        type,
        hours: rec?.hours,
      });
    }
  }

  return out;
}

export default function ApprovedShiftsPrintPage() {
  const { user } = useAuth();
  const q = useQuery();
  const navigate = useNavigate();

  const planId = q.get("planId") || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [plan, setPlan] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [visibleEmployees, setVisibleEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(""); // "" = celá pobočka

  useEffect(() => {
    if (!user?.firma) return;
    if (!planId) {
      setLoading(false);
      setError("Chybí planId v URL.");
      return;
    }

    const load = async () => {
      try {
        setError("");
        setLoading(true);

        const res = await fetch(
          `/api/shifts/by-id/${encodeURIComponent(planId)}?firma=${encodeURIComponent(user.firma)}`,
          {
            credentials: "include",
          }
        );

        if (!res.ok) {
          const t = await res.text();
          throw new Error(t || `HTTP ${res.status}`);
        }

        const data = await res.json();
        const { plan: p, shiftsRaw, employeesRaw } = extractPlanPayload(data);

        const flat = flattenShifts(shiftsRaw, employeesRaw);

        setPlan(p || null);
        setEmployees(Array.isArray(employeesRaw) ? employeesRaw : []);
        setShifts(flat);
      } catch (e) {
        console.error("ApprovedShiftsPrintPage load error:", e);
        setError("Nepodařilo se načíst schválený plán pro tisk.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.firma, planId]);

  useEffect(() => {
    const loadVisibleEmployees = async () => {
      try {
        if (user?.role !== "user" || !user?.firma) {
          setVisibleEmployees([]);
          return;
        }

        const targetPharmacyCode = plan?.pharmacyCode || user?.pharmacyCode;
        if (!targetPharmacyCode) {
          setVisibleEmployees([]);
          return;
        }

        const res = await fetch(
          `/api/employees/${encodeURIComponent(user.firma)}/${encodeURIComponent(
            targetPharmacyCode
          )}/visible`,
          { credentials: "include" }
        );

        if (!res.ok) {
          const t = await res.text();
          throw new Error(t || `HTTP ${res.status}`);
        }

        const data = await res.json();
        setVisibleEmployees(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("ApprovedShiftsPrintPage visible employees error:", e);
        setVisibleEmployees([]);
      }
    };

    loadVisibleEmployees();
  }, [user?.role, user?.firma, user?.pharmacyCode, plan?.pharmacyCode]);

  const year = plan?.year;
  const month = plan?.month;

  const monthTitle = useMemo(() => {
    if (!year || !month) return "Rozpis směn";
    return `Rozpis směn – ${fmtMonthName(year, month)} (v${plan?.version ?? ""})`;
  }, [year, month, plan?.version]);

  const pharmacyLabel = useMemo(() => {
    const pc = plan?.pharmacyCode || user?.pharmacyCode || "";
    return pc ? `Pobočka ${pc}` : "";
  }, [plan?.pharmacyCode, user?.pharmacyCode]);

  const printNameByEmployeeId = useMemo(() => {
    const m = new Map();

    if (user?.role !== "user") return m;

    const targetPharmacyCode = normalizeBranchCode(
      plan?.pharmacyCode || user?.pharmacyCode
    );
    if (!targetPharmacyCode) return m;

    for (const emp of visibleEmployees) {
      const id = String(emp?._id || emp?.employeeId || emp?.id || "").trim();
      if (!id) continue;

      const nickname = getNicknameForBranch(emp, targetPharmacyCode);
      if (nickname) {
        m.set(id, nickname);
      }
    }

    return m;
  }, [visibleEmployees, user?.role, user?.pharmacyCode, plan?.pharmacyCode]);

  // vytvoříme mapu: dateKey -> pole směn
  const shiftsByDay = useMemo(() => {
    const map = new Map();

    for (const s of shifts) {
      const raw = s?.date || s?.day || s?.datum || s?.dateKey;

      if (!raw) continue;

      const key = String(raw).slice(0, 10);

      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }

    // seřadit směny v rámci dne podle jména / nicku
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => {
        const aId = String(a?.employeeId || "").trim();
        const bId = String(b?.employeeId || "").trim();

        const an = String(
          printNameByEmployeeId.get(aId) || a?.employeeName || a?.fullName || ""
        ).toLowerCase();

        const bn = String(
          printNameByEmployeeId.get(bId) || b?.employeeName || b?.fullName || ""
        ).toLowerCase();

        return an.localeCompare(bn, "cs");
      });
      map.set(k, arr);
    }

    return map;
  }, [shifts, printNameByEmployeeId]);

  // role map: employeeId -> MAG / AS / SAN / OTHER
  const roleByEmployeeId = useMemo(() => {
    const m = new Map();
    if (!Array.isArray(employees)) return m;

    for (const e of employees) {
      const id = String(e?.employeeId || e?._id || e?.id || "");
      const pos = e?.position || e?.pozice || e?.role || "";
      if (id) m.set(id, normalizeRole(pos));
    }

    return m;
  }, [employees]);

  // zaměstnanci pro select:
  const employeeOptions = useMemo(() => {
    let opts = [];
    if (employees?.length) {
      opts = employees
        .map((e) => {
          const id = String(pickId(e));
          const officialName = pickName(e);
          const printName = printNameByEmployeeId.get(id) || officialName;

          return {
            id,
            name: printName,
          };
        })
        .filter((x) => x.id && x.name);
    }

    if (!opts.length) {
      const seen = new Map();
      for (const s of shifts) {
        const id = String(s?.employeeId || "");
        const officialName = String(s?.employeeName || s?.fullName || "");
        const printName = printNameByEmployeeId.get(id) || officialName;
        if (id && printName && !seen.has(id)) seen.set(id, printName);
      }
      opts = Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    }

    opts.sort((a, b) => a.name.localeCompare(b.name, "cs"));
    return opts;
  }, [employees, shifts, printNameByEmployeeId]);

  const weeks = useMemo(() => {
    if (!year || !month) return [];
    const { mondayIndex, daysInMonth } = getMonthMeta(year, month);

    const cells = [];
    for (let i = 0; i < mondayIndex; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(year, month - 1, d));
    }
    while (cells.length % 7 !== 0) cells.push(null);

    const out = [];
    for (let i = 0; i < cells.length; i += 7) {
      out.push(cells.slice(i, i + 7));
    }
    return out;
  }, [year, month]);

  const onPrint = () => {
    const rows = Array.isArray(weeks) ? weeks.length : 6;

    let z = 0.65;
    if (rows === 5) z = 0.72;
    else if (rows === 4) z = 0.8;
    else if (rows <= 3) z = 0.88;

    document.documentElement.style.setProperty("--print-zoom", String(z));
    document.documentElement.style.setProperty("--print-font", "12px");

    window.print();
  };

  const goBack = () => navigate(-1);

  if (loading) {
    return <div className="p-6 text-gray-500">Načítám rozpis...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 p-4">
          {error}
        </div>
        <button
          onClick={goBack}
          className="mt-4 px-4 py-2 rounded-xl border bg-white hover:bg-gray-50"
        >
          Zpět
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 print:p-0">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 7mm; }

          html, body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: #fff !important;
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          body, body * { visibility: hidden !important; }

          aside, nav, header, footer {
            display: none !important;
            visibility: hidden !important;
          }

          #print-root, #print-root * { visibility: visible !important; }

          #print-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-sizing: border-box !important;
            zoom: var(--print-zoom, 0.65) !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }

          #print-root .shadow,
          #print-root .shadow-sm,
          #print-root .shadow-md,
          #print-root .shadow-lg {
            box-shadow: none !important;
          }

          .no-print { display: none !important; }
          .print-only { display: block !important; }

          #print-root { font-size: var(--print-font, 13px) !important; }
          #print-root * { font-size: inherit !important; }
          #print-root .print-only .text-xl { font-size: 18px !important; }

          #print-root .divide-y > :not([hidden]) ~ :not([hidden]) {
            border-top-width: 0 !important;
          }

          #print-root {
            --gridLine: 2px;
            --gridColor: #111827;
            --bubbleLine: 1.5px;
            --bubbleColor: #6b7280;
          }

          #print-root > .bg-white.rounded-2xl.shadow.overflow-hidden,
          #print-root > .bg-white.rounded-2xl.shadow.border.overflow-hidden {
            border: var(--gridLine) solid var(--gridColor) !important;
            box-shadow: none !important;
          }

          #print-root > .bg-white.rounded-2xl.shadow.overflow-hidden > .grid.grid-cols-7,
          #print-root > .bg-white.rounded-2xl.shadow.border.overflow-hidden > .grid.grid-cols-7 {
            border-bottom: var(--gridLine) solid var(--gridColor) !important;
          }

          #print-root > .bg-white.rounded-2xl.shadow.overflow-hidden > .grid.grid-cols-7 > div,
          #print-root > .bg-white.rounded-2xl.shadow.border.overflow-hidden > .grid.grid-cols-7 > div {
            border-right: var(--gridLine) solid var(--gridColor) !important;
          }
          #print-root > .bg-white.rounded-2xl.shadow.overflow-hidden > .grid.grid-cols-7 > div:last-child,
          #print-root > .bg-white.rounded-2xl.shadow.border.overflow-hidden > .grid.grid-cols-7 > div:last-child {
            border-right: 0 !important;
          }

          #print-root > .bg-white.rounded-2xl.shadow.overflow-hidden .divide-y > div.grid.grid-cols-7,
          #print-root > .bg-white.rounded-2xl.shadow.border.overflow-hidden .divide-y > div.grid.grid-cols-7 {
            border-bottom: var(--gridLine) solid var(--gridColor) !important;
          }

          #print-root > .bg-white.rounded-2xl.shadow.overflow-hidden .divide-y > div.grid.grid-cols-7:last-child,
          #print-root > .bg-white.rounded-2xl.shadow.border.overflow-hidden .divide-y > div.grid.grid-cols-7:last-child {
            border-bottom: 0 !important;
          }

          #print-root > .bg-white.rounded-2xl.shadow.overflow-hidden .divide-y > div.grid.grid-cols-7 > div,
          #print-root > .bg-white.rounded-2xl.shadow.border.overflow-hidden .divide-y > div.grid.grid-cols-7 > div {
            border-right: var(--gridLine) solid var(--gridColor) !important;
          }
          #print-root > .bg-white.rounded-2xl.shadow.overflow-hidden .divide-y > div.grid.grid-cols-7 > div:last-child,
          #print-root > .bg-white.rounded-2xl.shadow.border.overflow-hidden .divide-y > div.grid.grid-cols-7 > div:last-child {
            border-right: 0 !important;
          }

          #print-root .rounded-md.border {
            border-width: var(--bubbleLine) !important;
          }

          #print-root .border-gray-200 {
            border-color: var(--bubbleColor) !important;
          }

          #print-root .border-gray-300 {
            border-color: var(--gridColor) !important;
          }
        }

        .print-only { display: none; }
      `}</style>

      <div className="no-print mb-6 bg-white rounded-2xl shadow border p-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-2xl font-extrabold text-gray-800">{monthTitle}</div>
            <div className="text-gray-500 mt-1">{pharmacyLabel}</div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div>
              <div className="text-sm text-gray-700 font-semibold mb-1">
                Zvýraznit zaměstnance
              </div>
              <select
                className="border rounded-xl px-3 py-2 min-w-[260px]"
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
              >
                <option value="">Celá pobočka</option>
                {employeeOptions.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={onPrint}
              className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700"
            >
              Tisk
            </button>

            <button
              onClick={goBack}
              className="px-4 py-2.5 rounded-xl border bg-white hover:bg-gray-50 font-semibold"
            >
              Zpět
            </button>
          </div>
        </div>
      </div>

      <div id="print-root" className="print-fit-one-page">
        <div className="print-only mb-3">
          <div className="text-xl font-extrabold">{monthTitle}</div>
          <div className="text-sm text-gray-700">{pharmacyLabel}</div>
        </div>

        <div className="bg-white rounded-2xl shadow border overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-300 text-gray-700 text-xs font-bold">
            {["Po", "Út", "St", "Čt", "Pá", "So", "Ne"].map((d, idx) => (
              <div
                key={d}
                className={[
                  "px-2 py-2 border-r border-gray-300 last:border-r-0",
                  idx >= 5 ? "bg-gray-100/80" : "",
                ].join(" ")}
              >
                {d}
              </div>
            ))}
          </div>

          <div className="divide-y">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7">
                {week.map((dateObj, di) => {
                  if (!dateObj) {
                    return (
                      <div
                        key={di}
                        className="min-h-[150px] border-r last:border-r-0 bg-gray-50/40"
                      />
                    );
                  }

                  const key = ymd(dateObj);
                  const dayShifts = shiftsByDay.get(key) || [];

                  const dow = dateObj.getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  const holidayName = getCzechHolidayName(dateObj);
                  const isHoliday = !!holidayName;

                  return (
                    <div
                      key={di}
                      className={[
                        "min-h-[150px] border-r border-gray-300 last:border-r-0 p-2",
                        isHoliday ? "bg-red-50/70" : isWeekend ? "bg-gray-50/70" : "",
                      ].join(" ")}
                    >
                      <div className="mb-1 min-w-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-extrabold text-gray-800 shrink-0">
                            {dateObj.getDate()}.
                          </span>

                          {isHoliday ? (
                            <span
                              className="text-[10px] font-semibold text-red-600 truncate overflow-hidden whitespace-nowrap"
                              title={holidayName}
                            >
                              {holidayName}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="space-y-1">
                        {(() => {
                          const roles = ["MAG", "AS"];

                          const bucket = {
                            MAG: { R: [], O: [], C: [], Q: [] },
                            AS: { R: [], O: [], C: [], Q: [] },
                            SAN: { any: [] },
                          };

                          const mkPerson = (s) => {
                            const empId = String(s?.employeeId || "");
                            const officialName = String(
                              s?.employeeName || s?.fullName || s?.name || ""
                            );
                            const displayName =
                              printNameByEmployeeId.get(empId) || officialName;

                            const rawType =
                              s?.type ?? s?.shiftType ?? s?.shift ?? s?.smena ?? "";
                            const type = normalizeShiftType(rawType) || "X";
                            const role = roleByEmployeeId.get(empId) || "OTHER";

                            const label = displayName
                              ? shortName(displayName)
                              : empId
                              ? empId.slice(-6)
                              : "—";

                            return {
                              empId,
                              empName: displayName || empId || "—",
                              officialName: officialName || empId || "—",
                              label,
                              type,
                              role,
                            };
                          };

                          for (const s of dayShifts) {
                            const p = mkPerson(s);

                            if (p.role === "MAG" || p.role === "AS") {
                              if (p.type === "C") bucket[p.role].C.push(p);
                              else if (p.type === "?") bucket[p.role].Q.push(p);
                              else if (p.type === "R") bucket[p.role].R.push(p);
                              else if (p.type === "O") bucket[p.role].O.push(p);
                            } else if (p.role === "SAN") {
                              bucket.SAN.any.push(p);
                            }
                          }

                          for (const r of roles) {
                            bucket[r].R.sort((a, b) => a.empName.localeCompare(b.empName, "cs"));
                            bucket[r].O.sort((a, b) => a.empName.localeCompare(b.empName, "cs"));
                            bucket[r].C.sort((a, b) => a.empName.localeCompare(b.empName, "cs"));
                            bucket[r].Q.sort((a, b) => a.empName.localeCompare(b.empName, "cs"));
                          }

                          bucket.SAN.any.sort((a, b) => a.empName.localeCompare(b.empName, "cs"));

                          const renderNames = (arr) => {
                            if (!arr.length) return <span className="text-gray-300">—</span>;

                            return (
                              <>
                                {arr.map((p, i) => {
                                  const isSelected =
                                    selectedEmployeeId && p.empId === selectedEmployeeId;
                                  const isDimmed =
                                    selectedEmployeeId && p.empId !== selectedEmployeeId;

                                  return (
                                    <span
                                      key={p.empId}
                                      className={[
                                        isSelected
                                          ? "font-semibold underline underline-offset-2"
                                          : "font-normal",
                                        isDimmed ? "opacity-40" : "opacity-100",
                                      ].join(" ")}
                                      title={
                                        p.officialName && p.officialName !== p.empName
                                          ? `${p.empName} (${p.officialName})`
                                          : p.empName
                                      }
                                    >
                                      {p.label}
                                      {i < arr.length - 1 ? ", " : ""}
                                    </span>
                                  );
                                })}
                              </>
                            );
                          };

                          const RowTag = ({ tag, arr }) => {
                            const isRowSelected =
                              selectedEmployeeId &&
                              Array.isArray(arr) &&
                              arr.some((p) => p?.empId === selectedEmployeeId);

                            return (
                              <div
                                className={[
                                  "h-[24px] flex items-center text-[11px] leading-tight rounded-md border border-gray-200 px-2",
                                  isRowSelected ? "bg-blue-100" : "bg-transparent",
                                ].join(" ")}
                              >
                                <span className="inline-block w-4 text-center text-gray-500 font-medium shrink-0 text-[11px] leading-tight">
                                  {tag}
                                </span>

                                <span className="ml-3 text-gray-800 min-w-0 flex-1 overflow-hidden whitespace-nowrap text-ellipsis pb-[1px]">
                                  {renderNames(arr)}
                                </span>
                              </div>
                            );
                          };

                          const RowSummaryDouble = ({ people }) => {
                            const isRowSelected =
                              selectedEmployeeId &&
                              Array.isArray(people) &&
                              people.some((p) => p?.empId === selectedEmployeeId);

                            const typeOrder = { R: 1, O: 2, C: 3, "?": 4, Q: 4 };

                            const sorted = [...(people || [])].sort((a, b) => {
                              const ao = typeOrder[a?.type] ?? 99;
                              const bo = typeOrder[b?.type] ?? 99;
                              if (ao !== bo) return ao - bo;
                              return String(a?.empName || "").localeCompare(
                                String(b?.empName || ""),
                                "cs"
                              );
                            });

                            const maxShown = 3;
                            const shown = sorted.slice(0, maxShown);
                            const rest = Math.max(0, sorted.length - shown.length);

                            const renderSummary = () => {
                              if (!sorted.length) return <span className="text-gray-300">—</span>;

                              return (
                                <>
                                  {shown.map((p, i) => {
                                    const isSelected =
                                      selectedEmployeeId && p.empId === selectedEmployeeId;
                                    const isDimmed =
                                      selectedEmployeeId && p.empId !== selectedEmployeeId;

                                    return (
                                      <span
                                        key={p.empId || `${p.label}-${i}`}
                                        className={[
                                          isSelected
                                            ? "font-semibold underline underline-offset-2 decoration-1"
                                            : "font-normal",
                                          isDimmed ? "opacity-40" : "opacity-100",
                                        ].join(" ")}
                                        title={
                                          p.officialName && p.officialName !== p.empName
                                            ? `${p.empName} (${p.officialName})`
                                            : p.empName
                                        }
                                      >
                                        {p.label}
                                        <span className="text-gray-500"> ({p.type})</span>
                                        {i < shown.length - 1 ? ", " : ""}
                                      </span>
                                    );
                                  })}
                                  {rest > 0 ? (
                                    <span className="text-gray-700 font-medium"> +{rest}</span>
                                  ) : null}
                                </>
                              );
                            };

                            return (
                              <div
                                className={[
                                  "h-[49px] flex items-center rounded-md border border-gray-200 px-2",
                                  isRowSelected ? "bg-blue-100" : "bg-transparent",
                                ].join(" ")}
                              >
                                <div className="text-gray-800 min-w-0 flex-1 overflow-hidden text-[11px] leading-tight pb-[1px]">
                                  <div className="max-h-[36px] overflow-hidden whitespace-normal break-words relative top-[0.5px]">
                                    {renderSummary()}
                                  </div>
                                </div>
                              </div>
                            );
                          };

                          const RowC = ({ arr }) => {
                            const isRowSelected =
                              selectedEmployeeId &&
                              Array.isArray(arr) &&
                              arr.some((p) => p?.empId === selectedEmployeeId);

                            return (
                              <div
                                className={[
                                  "h-[49px] flex items-center rounded-md border border-gray-200 px-2",
                                  isRowSelected ? "bg-blue-100" : "bg-transparent",
                                ].join(" ")}
                              >
                                <span className="inline-block w-4 text-center text-gray-500 font-medium shrink-0 text-[11px] leading-tight">
                                  C
                                </span>

                                <span className="ml-3 text-gray-800 min-w-0 flex-1 overflow-hidden whitespace-nowrap text-ellipsis text-[11px] leading-tight pb-[1px] relative top-[0.5px]">
                                  {renderNames(arr)}
                                </span>
                              </div>
                            );
                          };

                          const RowQ = ({ arr }) => {
                            const isRowSelected =
                              selectedEmployeeId &&
                              Array.isArray(arr) &&
                              arr.some((p) => p?.empId === selectedEmployeeId);

                            return (
                              <div
                                className={[
                                  "h-[49px] flex items-center rounded-md border border-gray-200 px-2",
                                  isRowSelected ? "bg-blue-100" : "bg-transparent",
                                ].join(" ")}
                              >
                                <span className="inline-block w-4 text-center text-gray-500 font-medium shrink-0 text-[11px] leading-tight">
                                  ?
                                </span>

                                <div className="ml-3 text-gray-800 min-w-0 flex-1 overflow-hidden text-[11px] leading-tight pb-[1px]">
                                  <div className="max-h-[36px] overflow-hidden whitespace-normal break-words relative top-[0.5px]">
                                    {renderNames(arr)}
                                  </div>
                                </div>
                              </div>
                            );
                          };

                          const RoleBlock = ({ role }) => {
                            const c = bucket[role].C || [];
                            const q = bucket[role].Q || [];
                            const r = bucket[role].R || [];
                            const o = bucket[role].O || [];

                            const totalPeople = c.length + q.length + r.length + o.length;

                            if (!totalPeople) {
                              return (
                                <div className="h-[49px] flex flex-col gap-[1px]">
                                  <RowTag tag="R" arr={[]} />
                                  <RowTag tag="O" arr={[]} />
                                </div>
                              );
                            }

                            if (totalPeople === 1 && q.length === 1) {
                              return <RowQ arr={q} />;
                            }

                            if (totalPeople === 1 && c.length === 1) {
                              return <RowC arr={c} />;
                            }

                            if (q.length >= 1 && totalPeople >= 2) {
                              if (totalPeople === 2 && q.length === 2) {
                                return <RowQ arr={q} />;
                              }
                              const all = [...r, ...o, ...c, ...q];
                              return <RowSummaryDouble people={all} />;
                            }

                            if (totalPeople >= 3) {
                              const all = [...r, ...o, ...c, ...q];
                              return <RowSummaryDouble people={all} />;
                            }

                            const rows = [];
                            if (r.length) rows.push({ tag: "R", arr: r });
                            if (o.length) rows.push({ tag: "O", arr: o });
                            if (c.length) rows.push({ tag: "C", arr: c });
                            if (q.length) rows.push({ tag: "?", arr: q });

                            const top = rows[0] || { tag: "R", arr: [] };
                            const bottom = rows[1] || { tag: "O", arr: [] };

                            return (
                              <div className="h-[49px] flex flex-col gap-[1px]">
                                <RowTag tag={top.tag} arr={top.arr} />
                                <RowTag tag={bottom.tag} arr={bottom.arr} />
                              </div>
                            );
                          };

                          const SanRow = () => {
                            if (!bucket.SAN?.any?.length) {
                              return <div className="h-[24px]" />;
                            }

                            const isRowSelected =
                              selectedEmployeeId &&
                              bucket.SAN.any.some((p) => p.empId === selectedEmployeeId);

                            return (
                              <div
                                className={[
                                  "h-[24px] flex items-center text-[11px] leading-tight rounded-md border border-gray-200 px-2",
                                  isRowSelected ? "bg-blue-100" : "bg-transparent",
                                ].join(" ")}
                              >
                                <div className="min-w-0 flex-1 overflow-hidden whitespace-nowrap text-ellipsis pb-[1px]">
                                  {bucket.SAN.any.map((p, i) => {
                                    const isSelected =
                                      selectedEmployeeId && p.empId === selectedEmployeeId;
                                    const isDimmed =
                                      selectedEmployeeId && p.empId !== selectedEmployeeId;

                                    return (
                                      <span
                                        key={p.empId || i}
                                        className="inline-flex items-center"
                                        title={
                                          p.officialName && p.officialName !== p.empName
                                            ? `${p.empName} (${p.officialName})`
                                            : p.empName
                                        }
                                      >
                                        <span className="inline-block w-4 text-center text-gray-500 font-medium shrink-0 text-[11px] leading-tight">
                                          {p.type}
                                        </span>

                                        <span
                                          className={[
                                            "ml-3 text-gray-800",
                                            isSelected
                                              ? "font-semibold underline underline-offset-2 decoration-1"
                                              : "font-normal",
                                            isDimmed ? "opacity-40" : "opacity-100",
                                          ].join(" ")}
                                        >
                                          {p.label}
                                        </span>

                                        {i < bucket.SAN.any.length - 1 ? (
                                          <span>,&nbsp;</span>
                                        ) : null}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          };

                          const hasAnything =
                            bucket.MAG.C.length ||
                            bucket.MAG.Q.length ||
                            bucket.MAG.R.length ||
                            bucket.MAG.O.length ||
                            bucket.AS.C.length ||
                            bucket.AS.Q.length ||
                            bucket.AS.R.length ||
                            bucket.AS.O.length ||
                            bucket.SAN.any.length;

                          if (!hasAnything) {
                            return <div className="text-[11px] text-gray-400">—</div>;
                          }

                          return (
                            <div className="space-y-1">
                              <RoleBlock role="MAG" />
                              <RoleBlock role="AS" />
                              <SanRow />
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
